import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MapPin, AlertTriangle } from "lucide-react";

// Shown on the client's order page ONLY when paid + in progress (parent decides visibility).
export function AddAddressOptions({ order, onDone }) {
  const opt = order.addon_options || {};
  const [open, setOpen] = useState(null); // "attempt_50" | "full_service_95"
  const [addr, setAddr] = useState({ street: "", city: "", state: "", zip: "" });
  const [busy, setBusy] = useState(false);

  const deadline = opt.deadline;
  const daysLeft = deadline ? Math.ceil((new Date(deadline).getTime() - Date.now()) / 86400000) : null;
  const deadlineClose = daysLeft !== null && daysLeft <= 3;

  const set = (k) => (e) => setAddr({ ...addr, [k]: e.target.value });

  const checkout = async () => {
    if (!addr.street.trim()) { toast.error("Enter the new service address."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.id || order._id}/add-address`, { option: open, new_address: addr });
      const { data: sess } = await api.post(`/invoices/${data.invoice_id}/stripe/checkout`, { origin_url: window.location.origin });
      const url = sess.url || sess.checkout_url;
      if (url) { window.location.href = url; return; }
      toast.success("Invoice created — complete payment to activate the address.");
      setOpen(null); onDone && onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start payment");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="add-address-options" className="rounded-xl border border-slate-200 bg-white p-5">
      <p className="font-display text-base font-bold text-slate-900">Add an address</p>
      <p className="mt-1 text-sm text-slate-500">Payment is required before the server sees a new address.</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button data-testid="add-address-attempt-btn" variant="outline" onClick={() => { setAddr({ street: "", city: "", state: "", zip: "" }); setOpen("attempt_50"); }}>
          <MapPin className="mr-1.5 h-4 w-4" /> Add address – 1 attempt ($50)
        </Button>
        <Button data-testid="add-address-full-btn" variant="outline" onClick={() => { setAddr({ street: "", city: "", state: "", zip: "" }); setOpen("full_service_95"); }}>
          <MapPin className="mr-1.5 h-4 w-4" /> Add address – full service ($95)
        </Button>
        {opt.barrier_reported && (
          <Button data-testid="replace-address-barrier-btn" variant="outline" onClick={() => toast.info("Use the access-barrier decision panel on this order to replace the address.")}>
            <MapPin className="mr-1.5 h-4 w-4" /> Replace address (barrier)
          </Button>
        )}
      </div>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent data-testid="add-address-dialog">
          <DialogHeader>
            <DialogTitle>{open === "full_service_95" ? "Add address – full service ($95)" : "Add address – 1 attempt ($50)"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600" data-testid="add-address-dates">
              <div>Court date: <span className="font-medium text-slate-800">{opt.court_date || "—"}</span></div>
              <div>Deadline: <span className="font-medium text-slate-800">{deadline || "—"}</span></div>
            </div>
            {deadlineClose && (
              <div data-testid="deadline-warning" className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                Deadline is close. Service at this address may not be completed in time.
              </div>
            )}
            <div><Label>Street address</Label><Input data-testid="add-address-street" value={addr.street} onChange={set("street")} placeholder="New service address" /></div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label>City</Label><Input data-testid="add-address-city" value={addr.city} onChange={set("city")} /></div>
              <div><Label>State</Label><Input data-testid="add-address-state" value={addr.state} onChange={set("state")} /></div>
              <div><Label>ZIP</Label><Input data-testid="add-address-zip" value={addr.zip} onChange={set("zip")} /></div>
            </div>
            <Button data-testid="add-address-pay-btn" disabled={busy} onClick={checkout} className="w-full bg-emerald-600 text-white hover:bg-emerald-700">
              {busy ? "Starting…" : `Continue to payment`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
